/**
 * Notification Service
 * --------------------
 * Manages email alert preferences and SMTP configuration for the Watch API.
 *
 * Endpoints:
 *   GET  /notifications/settings  → load alert preferences
 *   PUT  /notifications/settings  → save alert preferences
 *   GET  /notifications/smtp      → load SMTP configuration
 *   PUT  /notifications/smtp      → save SMTP configuration
 *   POST /notifications/test      → send a test email
 *
 * Auth: X-API-Key — same pattern as all other endpoints (handled by apiClient).
 *
 * Notes:
 *   - GET /notifications/smtp never returns the full password — always masked (e.g. "SG.abc***").
 *   - When saving SMTP, always send the current password field value back as-is.
 *     The backend detects the "***" suffix and preserves the original without overwriting.
 *   - POST /notifications/test always returns HTTP 200.
 *     Check the `sent` field in the response body to determine success or failure.
 *   - GET /notifications/settings returns defaults if never configured — no special handling needed.
 *   - GET /notifications/settings also returns `available_tactics` — the list of MITRE ATT&CK
 *     tactics the pipeline is capable of detecting. This is a system constant, not per-admin.
 *     The store keeps it separate from the editable settings form.
 */

import { apiClient } from "./apiClient";

// ---------------------------------------------------------------------------
// Default values — used as fallback if GET returns null
// ---------------------------------------------------------------------------

export const DEFAULT_SETTINGS = {
  enabled:     false,
  recipients:  [],
  threshold:   "high",
  tactics:     [],
  daily_digest: {
    enabled:   false,
    send_at:   "08:00",
  },
  monthly: {
    enabled:      false,
    send_on_day:  1,
  },
};

export const DEFAULT_SMTP = {
  host:     "",
  port:     587,
  user:     "",
  password: "",
  from:     "",
  tls:      true,
};

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

/**
 * Fetches the current alert notification preferences.
 * Returns DEFAULT_SETTINGS if the backend has no configuration yet.
 *
 * Also extracts `available_tactics` from the response and returns it separately
 * so the store can keep it apart from the editable form state.
 *
 * @returns {Promise<{ settings: object, availableTactics: Array }>}
 */
export async function getNotificationSettings() {
  try {
    const data = await apiClient("/notifications/settings");
    if (!data) return { settings: DEFAULT_SETTINGS, availableTactics: [] };

    const { available_tactics = [], ...rest } = data;
    return {
      settings:         { ...DEFAULT_SETTINGS, ...rest },
      availableTactics: available_tactics,
    };
  } catch (err) {
    console.error("[notificationService] Failed to load settings:", err);
    return { settings: DEFAULT_SETTINGS, availableTactics: [] };
  }
}

/**
 * Saves the alert notification preferences.
 * Strips `available_tactics` from the payload — it is read-only and must not be sent.
 *
 * @param {object} settings - Full settings object matching DEFAULT_SETTINGS shape
 * @returns {Promise<{ updated: boolean, updated_at: number } | null>}
 */
export async function saveNotificationSettings(settings) {
  const { available_tactics, ...body } = settings;
  return apiClient("/notifications/settings", {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// SMTP
// ---------------------------------------------------------------------------

/**
 * Fetches the current SMTP configuration.
 * The `password` field is always masked (e.g. "SG.abc***") — never the full value.
 * Returns DEFAULT_SMTP if the backend has no configuration yet.
 *
 * @returns {Promise<object>}
 */
export async function getSmtpSettings() {
  try {
    const data = await apiClient("/notifications/smtp");
    return data ?? DEFAULT_SMTP;
  } catch (err) {
    console.error("[notificationService] Failed to load SMTP settings:", err);
    return DEFAULT_SMTP;
  }
}

/**
 * Saves the SMTP configuration.
 *
 * Important: the request body uses `from_` (with underscore) because `from`
 * is a reserved word in Python. The GET response uses `from` (without underscore).
 * This function handles that mapping internally — callers always use `from`.
 *
 * Password handling: always send the current value of the password field.
 * If it contains "***", the backend will preserve the original without overwriting.
 *
 * @param {object} smtp - SMTP config object using `from` key (as returned by GET)
 * @returns {Promise<object | null>}
 */
export async function saveSmtpSettings(smtp) {
  const body = {
    host:     smtp.host,
    port:     Number(smtp.port),
    user:     smtp.user,
    password: smtp.password,
    from_:    smtp.from,   // GET returns `from`, PUT expects `from_`
    tls:      smtp.tls,
  };

  return apiClient("/notifications/smtp", {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Test email
// ---------------------------------------------------------------------------

/**
 * Sends a test email to the provided recipients.
 *
 * Always resolves (never throws) — check the `sent` field in the response.
 *
 * @param {string[]} recipients - List of email addresses
 * @returns {Promise<{ sent: boolean, recipients?: string[], sent_at?: number, error?: string }>}
 */
export async function sendTestEmail(recipients) {
  try {
    const data = await apiClient("/notifications/test", {
      method: "POST",
      body: JSON.stringify({ recipients }),
    });
    return data ?? { sent: false, error: "No response from server." };
  } catch (err) {
    console.error("[notificationService] Test email failed:", err);
    return { sent: false, error: err.message };
  }
}